// =================================================================
// CENTRALIZED COLOR CONFIGURATION
// =================================================================
// This file contains all color definitions for The Random Store
// Based on logo colors: #4abf18 (green) and #272735 (dark blue-gray)

// Logo Colors (Base)
export const LOGO_COLORS = {
  primary: '#4abf18',      // Logo green
  secondary: '#272735',    // Logo dark blue-gray
};

// =================================================================
// OPTION 1: SOPHISTICATED NEUTRAL (RECOMMENDED)
// Clean, professional, uses green sparingly for maximum impact
// =================================================================
export const THEME_SOPHISTICATED = {
  // Common colors (MUI compatibility + convenience)
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  // Primary brand colors
  primary: {
    main: '#4abf18',       // Logo green - use for CTAs and important elements
    light: '#6dd149',      // Lighter green
    dark: '#3a9915',       // Darker green
    contrastText: '#ffffff'
  },
  
  // Secondary colors
  secondary: {
    main: '#272735',       // Logo dark - navigation, headers
    light: '#3d3f52',      // Lighter version
    dark: '#1a1c26',       // Darker version
    contrastText: '#ffffff'
  },
  
  // Background colors
  background: {
    default: '#ffffff',    // Main background
    paper: '#ffffff',      // Card/paper background
    light: '#f8f9fa',      // Light sections
    dark: '#f1f3f4'        // Darker sections
  },
  
  // Text colors
  text: {
    primary: '#2d3436',    // Main text color
    secondary: '#636e72',  // Secondary text
    disabled: '#b2bec3',   // Disabled text
    light: '#ffffff',      // White text on dark backgrounds
    hint: '#74b9ff'        // Hint text
  },
  
  // Functional colors
  success: {
    main: '#27ae60',       // Success green (muted from logo green)
    light: '#2ecc71',
    dark: '#229954'
  },
  
  error: {
    main: '#e74c3c',       // Error red
    light: '#f1c0bd',
    dark: '#c0392b'
  },
  
  warning: {
    main: '#f39c12',       // Warning orange
    light: '#f8c471',
    dark: '#d68910'
  },
  
  info: {
    main: '#3498db',       // Info blue
    light: '#85c1e9',
    dark: '#2980b9'
  },
  
  // Border colors
  border: {
    light: '#e5e7eb',      // Light borders
    medium: '#d1d5db',     // Medium borders
    dark: '#9ca3af'        // Dark borders
  },
  
  // Action colors
  action: {
    hover: 'rgba(74, 191, 24, 0.04)',     // Green hover
    selected: 'rgba(74, 191, 24, 0.08)',   // Green selected
    disabled: 'rgba(0, 0, 0, 0.26)',       // Disabled
    focus: 'rgba(74, 191, 24, 0.12)'       // Focus
  }
};

// =================================================================
// OPTION 2: MODERN DARK THEME
// For dark mode or modern dark aesthetic
// =================================================================
export const THEME_DARK = {
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  primary: {
    main: '#4abf18',
    light: '#6dd149',
    dark: '#3a9915',
    contrastText: '#ffffff'
  },
  
  secondary: {
    main: '#272735',
    light: '#3d3f52',
    dark: '#1a1c26',
    contrastText: '#ffffff'
  },
  
  background: {
    default: '#1a1a1a',    // Very dark background
    paper: '#2d2d2d',      // Dark cards
    light: '#363636',      // Light sections
    dark: '#0f0f0f'        // Darker sections
  },
  
  text: {
    primary: '#ffffff',    // White text
    secondary: '#b0b0b0',  // Light gray text
    disabled: '#6b6b6b',   // Disabled text
    hint: '#1dd1a1'        // Hint text (teal)
  },
  
  accent: {
    teal: '#1dd1a1',       // Complementary teal
    purple: '#6c5ce7',     // Accent purple
    orange: '#fd79a8',     // Warm accent
    blue: '#74b9ff'        // Light blue
  }
};

// =================================================================
// OPTION 3: ULTRA MINIMAL
// Very clean, almost monochromatic with green accents
// =================================================================
export const THEME_MINIMAL = {
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  primary: {
    main: '#4abf18',
    light: '#6dd149',
    dark: '#3a9915',
    contrastText: '#ffffff'
  },
  
  secondary: {
    main: '#272735',
    light: '#3d3f52',
    dark: '#1a1c26',
    contrastText: '#ffffff'
  },
  
  // Neutral gray scale
  neutral: {
    50: '#f9fafb',         // Lightest
    100: '#f3f4f6',        // Very light
    200: '#e5e7eb',        // Light borders
    300: '#d1d5db',        // Medium light
    400: '#9ca3af',        // Medium
    500: '#6b7280',        // Text gray
    600: '#4b5563',        // Dark text
    700: '#374151',        // Very dark
    800: '#1f2937',        // Almost black
    900: '#111827'         // Black
  },
  
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    light: '#f9fafb',      // neutral.50
    medium: '#f3f4f6',     // neutral.100
    dark: '#e5e7eb'        // neutral.200
  },
  
  text: {
    primary: '#1f2937',    // neutral.800
    secondary: '#4b5563',  // neutral.600
    disabled: '#9ca3af',   // neutral.400
    light: '#ffffff'
  }
};

// =================================================================
// EXPORT DEFAULT THEME (Change this to switch themes)
// =================================================================
export const COLORS = THEME_SOPHISTICATED; // <-- Change this line to switch themes

// =================================================================
// UTILITY FUNCTIONS
// =================================================================

// Function to get color with opacity
export const withOpacity = (color, opacity) => {
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Function to generate hover color (slightly darker)
export const getHoverColor = (color) => {
  // Simple darkening - in a real app you might want a more sophisticated approach
  return color.replace('#', '#').concat('dd'); // Adds transparency
};

// Gradient helpers
export const GRADIENTS = {
  primary: `linear-gradient(135deg, ${COLORS.primary.light}, ${COLORS.primary.main})`,
  secondary: `linear-gradient(135deg, ${COLORS.secondary.light}, ${COLORS.secondary.main})`,
  success: `linear-gradient(135deg, ${COLORS.success.light}, ${COLORS.success.main})`,
  subtle: `linear-gradient(135deg, ${COLORS.background.light}, ${COLORS.background.default})`
};

export default COLORS;
